// React Dependencies
import React, { useState, useEffect, useRef } from 'react';

// External Dependencies
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import MaterialTable from 'material-table';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import Box from '@material-ui/core/Box';
import InputAdornment from '@material-ui/core/InputAdornment';
import Search from '@material-ui/icons/Search';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import _ from 'lodash';

// Internal Dependencies
import { IsEmpty } from '../../common/checks';
import DateRangePicker from '../global/form/DateRangePicker';
import ViewPostReviews from './viewPosts';

import { getPaginateTags, updateTag } from '../../actions/admin/tagAction';
import { toggleModal, updateModalContent } from '../../actions/modal';
import Updating from './create';
import { formatDateToDayMonthNameYear, showDateInSGTime } from '../../common/util';

const themeTable = createMuiTheme({
  overrides: {
    MuiTableRow: {
      root: {
        '&:hover': {
          transition:      '0.3s ease',
          backgroundColor: '#42d1f5',
        },
      },
    },
  },
});

const TagsTable = ({ getPaginateTags, updateTag, toggleModal, updateModalContent }) => {
  const table = useRef(null);
  const formCreateRef = useRef();

  const initializeFocusValue = {
    searchFocus: false,
    nameFocus:   false,
  };

  const [tag, setTag] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('pending');
  const [currentUsedInPostStatus, setCurrentUsedInPostStatus] = useState(null);
  const [currentUsedInReviewStatus, setCurrentUsedInReviewStatus] = useState(null);
  const [currentUsedInPoiStatus, setCurrentUsedInPoiStatus] = useState(null);
  const [currentOrder, setCurrentOrder] = useState('');

  const [pageSize, setPageSize] = useState(20);
  const [autoFocus, setAutoFocus] = useState(initializeFocusValue);

  const [search, setSearch] = useState('');
  const [name, setName] = useState('');
  const { searchFocus, nameFocus } = autoFocus;

  const [onResetSearch, setOnResetSearch] = useState(false);
  const [onResetName, setOnResetName] = useState(false);

  const [tagName, setTagName] = useState('');
  const [dateRange, setDateRange] = useState(null);

  const columns = [
    {
      title:   'Name',
      field:   'name',
      sorting: true,
    },
    {
      title:   'Name (Chinese Zh)',
      field:   'nameChinese',
      sorting: false,
      render:  (tags) => (tags.nameZh === null ? '-' : tags.nameZh),
    },
    {
      title:   'Updated Date',
      field:   'updatedAt',
      sorting: false,
      render:  (tags) => (tags.updatedAt !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(tags.updatedAt), true)}` : '-'),
    },
    {
      title:   'Used in Post',
      field:   'isUsedInPost',
      sorting: false,
      render:  (tags) => (tags.isUsedInPost ? 'Yes' : tags.isUsedInPost === false ? 'No' : '-'),
    },
    {
      title:   'Used in Review',
      field:   'isUsedInReview',
      sorting: false,
      render:  (tags) => (tags.isUsedInReview ? 'Yes' : tags.isUsedInReview === false ? 'No' : '-'),
    },
    {
      title:   'Used in Poi',
      field:   'isUsedInPoi',
      sorting: false,
      render:  (tags) => (tags.isUsedInPoi ? 'Yes' : tags.isUsedInPoi === false ? 'No' : '-'),
    },
    {
      title:   'Status',
      field:   'isApproved',
      sorting: false,
      render:  (tags) => {
        if (tags.isApproved === true) {
          return (
            <div className="badge badge-success p-2 pr-3 pl-3">Approved</div>
          );
        } else if (tags.isApproved === false) {
          return (
            <div className="badge badge-danger p-2 pr-3 pl-3">Rejected</div>
          );
        } else {
          return (
            <div className="badge badge-warning p-2 pr-3 pl-3">Pending</div>
          );
        }
      },
    },
  ];

  useEffect(() => {
    if (!onResetSearch) {
      table.current.onQueryChange();
    } else {
      setOnResetSearch(false);
    }
  }, [search]);

  useEffect(() => {
    if (!onResetName) {
      table.current.onQueryChange();
    } else {
      setOnResetName(false);
    }
  }, [name]);

  useEffect(() => {
    table.current.onQueryChange();
  }, [tagName, currentUsedInPostStatus, currentUsedInReviewStatus, currentUsedInPoiStatus, currentStatus, currentOrder, dateRange]);

  const updateDataTable = () => {
    if (table.current) {
      table.current.onQueryChange();
    }
  };

  const onApproveTag = async (tagToUpdate) => {
    try {
      await updateTag({ name: tagToUpdate, isApproved: true });
      toast.success('Tag has been Approved');
      updateDataTable();
    } catch (error) {
      console.log(error);
    }
  };

  const onRejectTag = async (tagToUpdate) => {
    try {
      await updateTag({ name: tagToUpdate, isApproved: false });
      toast.warn('Tag has been Rejected');
      updateDataTable();
    } catch (err) {
      toast.error(err.message);
    }
  };

  function handleSort() {
    if (currentOrder) {
      if (currentOrder === 'DESC') {
        setCurrentOrder('ASC');
      } else {
        setCurrentOrder('DESC');
      }
    } else {
      setCurrentOrder('ASC');
    }
  }

  const handleChange = (event) => {
    if (event.target.name === 'tagStatus') {
      setCurrentStatus(event.target.value);
    } else if (event.target.name === 'usedInPostStatus') {
      setCurrentUsedInPostStatus(event.target.value);
    } else if (event.target.name === 'usedInReviewStatus') {
      setCurrentUsedInReviewStatus(event.target.value);
    } else if (event.target.name === 'usedInPoiStatus') {
      setCurrentUsedInPoiStatus(event.target.value);
    }
  };

  const handleSearchChange = _.debounce((value) => {
    setSearch(value);
    setAutoFocus({ nameFocus: false, searchFocus: true });
  }, 500);

  const onUpdateTags = (data) => {
    updateModalContent({
      title:        'Update Tag',
      content:      <Updating updateTag={data} updateList={updateDataTable} ref={formCreateRef} />,
      actionButton: {
        actionText: 'Save',
        actionType: 'primary',
        action:     () => formCreateRef.current.submitForm(),
      },
      cancelText: 'Cancel',
    });

    toggleModal();
  };

  const setDatesFilter = (dates) => {
    if (!IsEmpty(dates) && dates.length > 1) {
      const arrDate1 = new Date(dates[0]);
      const arrDate2 = new Date(dates[1]);
      const dateFrom = `${arrDate1.getFullYear()}-${(arrDate1.getMonth() + 1).toString().padStart(2, '0')}-${arrDate1.getDate().toString().padStart(2, '0')}`;
      const dateTo = `${arrDate2.getFullYear()}-${(arrDate2.getMonth() + 1).toString().padStart(2, '0')}-${arrDate2.getDate().toString().padStart(2, '0')}`;
      setDateRange([dateFrom, dateTo]);
    } else {
      setDateRange([]);
    }
  };

  const handleViewPost = (key) => {
    setTag(key);
    setOpenModal(true);
  };

  return (
    <div>
      { openModal && (
        <ViewPostReviews
          tag={tag}
          changeOpenModal={() => {
            setOpenModal(false);
            setTag(null);
          }}
        />
      )}

      <ThemeProvider theme={themeTable}>
        <MaterialTable
          hover
          tableRef={table}
          className="material-table"
          title=""
          columns={columns}
          icons={{
            SortArrow: () => (currentOrder === '' ? <UnfoldMoreIcon onClick={handleSort} />
              : currentOrder === 'ASC' ? <ArrowUpwardIcon onClick={handleSort} />
                : <ArrowDownwardIcon onClick={handleSort} />),
          }}
          onChangeRowsPerPage={(pageSize) => {
            setCurrentOrder('');
            setPageSize(pageSize);
            setAutoFocus({ nameFocus: false, searchFocus: false });
          }}
          data={(query) => new Promise(async (resolve, reject) => {
            try {
              query.name = search;
              query.status = currentStatus;
              query.isUsedInPost = currentUsedInPostStatus;
              query.isUsedInReview = currentUsedInReviewStatus;
              query.isUsedInPoi = currentUsedInPoiStatus;
              query.orderDir = currentOrder;
              query.dateRange = dateRange;
              const result = await getPaginateTags(query);
              let { rows, totalCount } = result;
              let { page } = query;

              if (rows.length === 0) {
                page = 0;

                if (totalCount !== 0) {
                  const resultFirstPage = await getPaginateTags({ ...query, page });
                  rows = resultFirstPage.rows;
                }
              }

              resolve({
                data: rows,
                page,
                totalCount,
              });
            } catch (err) {
              toast.error(err);
              resolve({
                data:       [],
                page:       0,
                totalCount: 0,
              });
            }
          })}
          actions={[
            /* overridden */
            {
              icon:    'check',
              tooltip: 'Approve',
              onClick: (event, tag) => { },
            },
          ]}
          options={{
            actionsColumnIndex:  -1,
            pageSize,
            emptyRowsWhenPaging: false,
            filtering:           false,
            search:              false,
            cellStyle:           {
              fontSize: 14,
            },
          }}
          components={{
            Toolbar: () => (
              <div>
                <div>
                  <Box display="flex" justifyContent="flex-end" mt={2} p={1} bgcolor="background.paper">
                    <Box p={1} width="25%" style={{ marginTop: '16px' }}>
                      <FormControl fullWidth>
                        <Input
                          placeholder="Search by name"
                          defaultValue={search}
                          onChange={(e) => { handleSearchChange(e.target.value); }}
                          type="search"
                          startAdornment={(
                            <InputAdornment position="start">
                              <Search />
                            </InputAdornment>
                          )}
                          autoFocus={searchFocus}
                        />
                      </FormControl>
                    </Box>

                    <Box p={1} width="25%">
                      <FormControl fullWidth>
                        <InputLabel id="status-filter-label">Used in Post status</InputLabel>
                        <Select
                          labelId="status-filter-label"
                          value={currentUsedInPostStatus}
                          name="usedInPostStatus"
                          onChange={handleChange}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value>Yes</MenuItem>
                          <MenuItem value={false}>No</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <Box p={1} width="25%">
                      <FormControl fullWidth>
                        <InputLabel id="status-filter-label">Used in Review status</InputLabel>
                        <Select
                          labelId="status-filter-label"
                          value={currentUsedInReviewStatus}
                          name="usedInReviewStatus"
                          onChange={handleChange}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value>Yes</MenuItem>
                          <MenuItem value={false}>No</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <Box p={1} width="25%">
                      <FormControl fullWidth>
                        <InputLabel id="status-filter-label">Used in Poi</InputLabel>
                        <Select
                          labelId="status-filter-label"
                          value={currentUsedInPoiStatus}
                          name="usedInPoiStatus"
                          onChange={handleChange}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value>Yes</MenuItem>
                          <MenuItem value={false}>No</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <Box p={1} width="25%">
                      <FormControl fullWidth>
                        <InputLabel id="status-filter-label">Tag status</InputLabel>
                        <Select
                          labelId="status-filter-label"
                          value={currentStatus}
                          name="tagStatus"
                          onChange={handleChange}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="approved">Approved</MenuItem>
                          <MenuItem value="rejected">Rejected</MenuItem>
                          <MenuItem value="pending">Pending</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  <Box display="flex" justifyContent="flex-start" p={1} bgcolor="background.paper">
                    <Box p={1} width="25%">
                      <InputLabel>Set a date range</InputLabel>
                      <DateRangePicker
                        callback={(d) => setDatesFilter(d)}
                        dateVal={dateRange}
                      />
                    </Box>
                  </Box>
                </div>
              </div>
            ),

            Action: (props) => (
              <div>
                <Box display="flex" flexDirection="row" justifyContent="flex-end">
                  {
                    (props.data.isApproved === false || props.data.isApproved === null) ? <Box p={1} alignSelf="center"><Button onClick={(event) => { onApproveTag(props.data.name); }} color="primary" variant="contained" style={{ textTransform: 'none', fontSize: 9 }} size="small">Approve</Button></Box> : ''
                  }

                  {
                    (props.data.isApproved === true || props.data.isApproved === null) ? <Box p={1} alignSelf="center"><Button onClick={(event) => { onRejectTag(props.data.name); }} color="secondary" variant="contained" style={{ textTransform: 'none', fontSize: 9 }} size="small">Reject</Button></Box> : ''
                  }

                  <Box p={1} alignSelf="center">
                    <Button onClick={() => onUpdateTags(props.data)} color="#1de9b6" variant="contained" style={{ textTransform: 'none', fontSize: 9 }} size="small">Update</Button>
                  </Box>

                  <Box p={1} alignSelf="center">
                    <Button onClick={() => handleViewPost(props.data.name)} color="#1de9b6" variant="contained" style={{ textTransform: 'none', fontSize: 9 }} size="small">Posts, Reviews & Pois</Button>
                  </Box>

                </Box>
              </div>
            ),
          }}
        />
      </ThemeProvider>
    </div>
  );
};

// map props to redux state (use Chrome Redux DevTools to help identify how state are stored)
function mapStateToProps(state) {
  return {
    tags: state.admin.tag.tags,
  };
}

// map the redux action creators to props
const mapActionToProps = {
  updateTag, getPaginateTags,
  toggleModal, updateModalContent,
};

// connect(mapStateToProps, actions to component as props)(component)
export default connect(
  mapStateToProps,
  mapActionToProps,
)(TagsTable);
